import { useStorage } from '@vueuse/core'
interface User {
  id?: number
  email?: string
  api_token?: string
  tenant_identifier?: string
  api_tokenV2?: string
  tenant_identifierV2?: string
  mid?: number
}
export const useUserStore = defineStore('userStore', () => {
  const toast = useToast()

  const { apiBaseUrl, apiBaseUrlV2 } = useRuntimeConfig().public
  const user = ref(useStorage('user', {} as User, localStorage))
  const calendarCategories = ref()
  const hasSupervisor = ref<boolean | null>(null)

  const contact = ref(
    useStorage('contact', {} as Contact & { token?: string; tenantId?: string }, localStorage)
  )

  const isLoggedIn = computed(() => !!user.value.api_token)
  const isLoggedInAsContact = computed(() => !!contact.value.token)
  const userEmail = computed(() => user.value.email ?? '-')
  const userProfilePicture = computed(() =>
    user?.value?.id
      ? `https:\\/\\/my.1tool.com\\/crm_files\\/users\\/${user.value.id}\\/logo.jpg`
      : 'https:\\/\\/my.1tool.com\\/images\\/standard_picture.jpg'
  )

  async function login(email: string, password: string): Promise<boolean> {
    const responseV2 = (await fetch(`${apiBaseUrlV2}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!responseV2.ok) {
      throw new Error(responseV2.status)
    }

    const response = (await fetch(`${apiBaseUrl}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response.ok) {
      throw new Error(response.status)
    }

    if (responseV2 && responseV2.status === 200 && response && response.status === 200) {
      const responseText = await response.text()
      const responseTextV2 = await responseV2.text()

      if (responseText && responseTextV2) {
        const { api_token: apiTokenV2, tenant_identifier: tenantIdentifierV2 } =
          JSON.parse(responseTextV2)
        const {
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          id
        } = JSON.parse(responseText)

        user.value = {
          id,
          email,
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          api_tokenV2: apiTokenV2,
          tenant_identifierV2: tenantIdentifierV2
        }

        if (!calendarCategories.value) await fetchCalendarCategories()

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  function logout() {
    user.value = {}
    const contactValue = { ...contact.value }

    window?.localStorage?.clear()
    contact.value = contactValue
    navigateTo('/login')
  }

  async function fetchCalendarCategories() {
    if (!user.value.id) return
    try {
      const response = await useApi(`calendar-categories?filter[user_id]=${user.value.id}`, 'GET')
      calendarCategories.value = response?.data ?? []
    } catch {}
  }

  function setHasSupervisor(hasSupervisorParam: boolean | null) {
    hasSupervisor.value = hasSupervisorParam
  }

  /** Loads once whether the user reports to a supervisor; stays null while that is unknown. */
  async function loadHasSupervisor() {
    if (hasSupervisor.value !== null || !user.value.id) return hasSupervisor.value

    const result = await useApi(`users/${user.value.id}`, 'GET')
    if (result?.data) hasSupervisor.value = !!result.data.supervisor

    return hasSupervisor.value
  }

  return {
    user,
    userEmail,
    userProfilePicture,
    isLoggedIn,
    calendarCategories,
    hasSupervisor,
    contact,
    isLoggedInAsContact,
    fetchCalendarCategories,
    setHasSupervisor,
    loadHasSupervisor,
    login,
    logout
  }
})
// Use HMR for this store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
